import { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Send, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { KnowledgeFrame } from '../components/KnowledgeNav'
import { RuleStatus } from '../components/ConceptCuration'
import { describeRule } from '../components/ruleText'
import { useExportRules, useRevokeRule, useRules } from '../hooks/useKnowledge'

const LABEL: Record<string, string> = { merge: 'Merge', separate: 'Split', reject: 'Remove' }

export function RulesPage() {
  const [includeRevoked, setIncludeRevoked] = useState(false)
  const { data, isLoading } = useRules(includeRevoked)
  const revoke = useRevokeRule()
  const exportRules = useExportRules()

  return (
    <KnowledgeFrame
      title="Rules"
      description={
        <>
          Merges, splits and removals are rules, not one-off edits: they are sent to Picasso&apos;s graph
          build every night (03:15 UTC, before the 03:30 build) and applied on every build from then
          on. They are stored against the concepts&apos; names, so they keep meaning the same thing after
          a rebuild. A newer rule replaces an older one it contradicts. Category, weight, renames and
          hides are sent too, for search, but they already apply here.
        </>
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-500">
          {data?.lastExportAt
            ? <>Last sent to the build {formatDistanceToNow(new Date(data.lastExportAt), { addSuffix: true })}.</>
            : 'Not sent to the build yet.'}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={includeRevoked} onCheckedChange={setIncludeRevoked} /> Show revoked
          </label>
          <Button size="sm" variant="outline" disabled={exportRules.isPending} onClick={() => exportRules.mutate()}>
            <Send className="mr-1.5 h-3.5 w-3.5" /> Send to the build now
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{includeRevoked ? 'All rules' : 'Open rules'}</CardTitle>
          <CardDescription>Status is the last build&apos;s report on each rule.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rule</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Decided</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={4}><Skeleton className="h-5 w-full" /></TableCell></TableRow>
              )}
              {!isLoading && data?.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-sm text-slate-500">
                    No rules yet. Merge, split or remove concepts from a concept page or the Review queue.
                  </TableCell>
                </TableRow>
              )}
              {data?.data.map((r) => (
                <TableRow key={r.id} className={r.revokedAt ? 'opacity-50' : undefined}>
                  <TableCell>
                    <span className="mr-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-600">{LABEL[r.action]}</span>
                    <Link to={`/app/knowledge/${r.ucids[0]}`} className="hover:underline">{describeRule(r)}</Link>
                  </TableCell>
                  <TableCell className="max-w-xs"><RuleStatus rule={r} /></TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500">
                    {r.decidedByEmail ?? 'unknown'}<br />
                    {formatDistanceToNow(new Date(r.decidedAt), { addSuffix: true })}
                  </TableCell>
                  <TableCell className="text-right">
                    {!r.revokedAt && (
                      <Button size="sm" variant="ghost" disabled={revoke.isPending} onClick={() => revoke.mutate(r.id)}>
                        <Undo2 className="mr-1 h-3.5 w-3.5" /> Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </KnowledgeFrame>
  )
}
